import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PlanCode = 'free' | 'premium_monthly' | 'premium_yearly';
export type SubscriptionStatus = 'free' | 'trialing' | 'active' | 'past_due' | 'canceled';

export interface UserSubscription {
  plan_code: PlanCode;
  status: SubscriptionStatus;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_end: string | null;
}

export const FREE_MATCH_LIMIT = 50;
export const MATCH_LIMIT_NUDGE_THRESHOLD = 40;
// Feature flag: free-tier enforcement is paused. Flip back to true to restore gating.
export const FREE_TIER_ENFORCED = false;

export interface Entitlements {
  isPremium: boolean;
  plan: PlanCode;
  status: SubscriptionStatus;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  // Match usage
  matchesPlayed: number;
  matchLimit: number; // 50 for free, Infinity for premium
  matchesRemaining: number;
  isOverMatchLimit: boolean;
  shouldNudge: boolean;
  isLoading: boolean;
  // Deprecated fields kept for backward-compat with existing components
  isInIntroPeriod: boolean;
  introPeriodEndsAt: string | null;
  daysLeftInIntro: number;
  tournamentsJoinedThisYear: number;
  pollsCreatedThisMonth: number;
  groupsJoined: number;
}

export function useEntitlements(): Entitlements {
  const { user } = useAuth();

  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['user-subscription', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('plan_code, status, current_period_end, cancel_at_period_end, trial_end')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) {
        console.error('Error loading subscription:', error);
        return null;
      }
      return data as UserSubscription | null;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const { data: matchesPlayed = 0, isLoading: matchesLoading } = useQuery({
    queryKey: ['user-match-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { data, error } = await supabase.rpc('get_user_match_count', { _user_id: user.id });
      if (error) {
        console.error('Error loading match count:', error);
        return 0;
      }
      return (data as number) ?? 0;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const plan: PlanCode = (subscription?.plan_code ?? 'free') as PlanCode;
  const status: SubscriptionStatus = (subscription?.status ?? 'free') as SubscriptionStatus;
  const isPremium =
    (status === 'active' || status === 'trialing') &&
    (plan === 'premium_monthly' || plan === 'premium_yearly') &&
    (!subscription?.current_period_end || new Date(subscription.current_period_end) > new Date());

  const matchLimit = isPremium || !FREE_TIER_ENFORCED ? Infinity : FREE_MATCH_LIMIT;
  const matchesRemaining = isPremium || !FREE_TIER_ENFORCED ? Infinity : Math.max(0, FREE_MATCH_LIMIT - matchesPlayed);
  const isOverMatchLimit = FREE_TIER_ENFORCED && !isPremium && matchesPlayed >= FREE_MATCH_LIMIT;
  const shouldNudge = FREE_TIER_ENFORCED && !isPremium && matchesPlayed >= MATCH_LIMIT_NUDGE_THRESHOLD && matchesPlayed < FREE_MATCH_LIMIT;

  return {
    isPremium,
    plan,
    status,
    cancelAtPeriodEnd: subscription?.cancel_at_period_end ?? false,
    currentPeriodEnd: subscription?.current_period_end ?? null,
    matchesPlayed,
    matchLimit,
    matchesRemaining,
    isOverMatchLimit,
    shouldNudge,
    isLoading: subLoading || matchesLoading,
    // Legacy no-ops
    isInIntroPeriod: false,
    introPeriodEndsAt: null,
    daysLeftInIntro: 0,
    tournamentsJoinedThisYear: 0,
    pollsCreatedThisMonth: 0,
    groupsJoined: 0,
  };
}

/** Map a Postgres error to a friendly upgrade prompt. */
export function parseFreeTierError(err: unknown): { code: string; message: string } | null {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('match_limit_reached')) {
    return {
      code: 'matches',
      message: `You've played your ${FREE_MATCH_LIMIT} free matches. Upgrade to Premium to keep playing.`,
    };
  }
  return null;
}
